import { useParams, Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ProjectForm from "@/components/ProjectForm";
import { useState } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { ProjectHeader } from "@/components/projects/ProjectHeader";
import { ProjectHeaderSection } from "@/components/project/ProjectHeaderSection";
import { ProjectTabs } from "@/components/project/ProjectTabs";
import { ShareDialog } from "@/components/share/ShareDialog";
import type { Project } from "@/types/project";
import type { SharePermission } from "@/types/share";
import { useTranslation } from 'react-i18next';

export default function Project() {
  const { projectId } = useParams();
  const [showEditForm, setShowEditForm] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: project, isLoading, error, refetch } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      if (!projectId) throw new Error("No project ID provided");

      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .maybeSingle();

      if (error) {
        console.error("Project: Supabase error", error);
        toast.error(t('failed_load_project'));
        throw error;
      }

      if (!data) {
        return null;
      }

      const transformedData: Project = {
        id: data.id,
        name: data.name,
        description: data.description,
        camera_budget: data.camera_budget || null,
        ge_budget: data.ge_budget || null,
        general_notes: data.general_notes || null,
        scout_photos: Array.isArray(data.scout_photos) 
          ? data.scout_photos.map(String)
          : [],
        documents: Array.isArray(data.documents) 
          ? data.documents.map((doc: any) => ({
              name: typeof doc === 'object' && doc !== null && 'name' in doc ? String(doc.name) : '',
              url: typeof doc === 'object' && doc !== null && 'url' in doc ? String(doc.url) : ''
            }))
          : [],
        prep_date: data.prep_date || null,
        pre_light_date: data.pre_light_date || null,
        user_id: data.user_id,
        budget_enabled: data.budget_enabled ?? true,
        custom_categories: Array.isArray(data.custom_categories) 
          ? data.custom_categories.map(String)
          : [],
        shoot_dates: data.shoot_dates || [],
        camera_spent: data.camera_spent || 0,
        ge_spent: data.ge_spent || 0,
        status: data.status || null,
        camera_notes: data.camera_notes || null,
        ge_notes: data.ge_notes || null,
        cost_tracking_only: data.cost_tracking_only || false,
        archived_at: data.archived_at || null,
        camera_enabled: data.camera_enabled ?? true,
        ge_enabled: data.ge_enabled ?? true,
        updated_at: data.updated_at || null
      };

      return transformedData;
    },
    enabled: !!projectId,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  const { data: projectShare } = useQuery({
    queryKey: ["project_share", projectId, user?.id],
    queryFn: async () => {
      if (!user || !projectId) return null;
      
      const { data, error } = await supabase
        .from("project_shares")
        .select("permission")
        .eq("project_id", projectId)
        .eq("shared_with", user.id)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching project share:", error);
        return null;
      }
      
      return data as { permission: SharePermission } | null;
    },
    enabled: !!projectId && !!user,
  });

  const handleProjectUpdate = async (updatedProject: Partial<Project>) => {
    if (!canEdit) {
      toast.error(t('no_permission_edit_project'));
      return;
    }

    try {
      console.log('Updating project with dates:', {
        prep_date: updatedProject.prep_date,
        pre_light_date: updatedProject.pre_light_date,
        shoot_dates: updatedProject.shoot_dates,
      });

      const { error } = await supabase
        .from('projects')
        .update({
          name: updatedProject.name,
          description: updatedProject.description,
          camera_budget: updatedProject.camera_budget,
          ge_budget: updatedProject.ge_budget,
          budget_enabled: updatedProject.budget_enabled,
          prep_date: updatedProject.prep_date,
          pre_light_date: updatedProject.pre_light_date,
          shoot_dates: updatedProject.shoot_dates || [],
          custom_categories: updatedProject.custom_categories || [],
          scout_photos: updatedProject.scout_photos || [],
          camera_enabled: updatedProject.camera_enabled,
          ge_enabled: updatedProject.ge_enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId);

      if (error) {
        console.error('Supabase update error:', error);
        toast.error(error.message || t('failed_update_project'));
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      
      toast.success(t('project_updated'));
      setShowEditForm(false);
    } catch (err) {
      console.error('Update error:', err);
      toast.error(t('unexpected_error'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen site-gradient">
        <div className="container mx-auto py-2 px-3">
          <ProjectHeader />
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-white/70 animate-pulse">{t('loading_project')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen site-gradient">
        <div className="container mx-auto py-2 px-3">
          <ProjectHeader />
          <div className="text-white/70 mt-4 p-6 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm shadow-lg">
            <h2 className="text-xl font-bold text-white mb-2">{t('error')}</h2>
            <p>{t('problem_loading_project')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen site-gradient">
        <div className="container mx-auto py-2 px-3">
          <ProjectHeader />
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-white/70 p-8 bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm shadow-lg text-center">
              <h2 className="text-2xl font-bold text-white mb-2">{t('project_not_found')}</h2>
              <p>{t('project_not_found_desc')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = project?.user_id === user?.id || projectShare?.permission === "edit";

  return (
    <div className="min-h-screen site-gradient">
      <div className="container mx-auto py-4 px-3">
        <div className="max-w-[1400px] mx-auto">
          <ProjectHeader />
          
          <ProjectHeaderSection
            project={project}
            isPublicView={false}
            canEdit={canEdit}
            onEditClick={() => setShowEditForm(true)}
            onShareClick={() => setShowShareDialog(true)}
          />

          {showEditForm && (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <ProjectForm
                onClose={() => setShowEditForm(false)}
                onSubmit={handleProjectUpdate}
                initialData={project}
              />
            </div>
          )}

          <ShareDialog
            projectId={projectId}
            open={showShareDialog}
            onOpenChange={setShowShareDialog}
          />

          <ProjectTabs
            projectId={projectId}
            project={project}
            canEdit={canEdit}
          />
        </div>
      </div>
    </div>
  );
}
